import React, { useCallback, useEffect } from 'react';
import { Button, InputWraper, useForm } from 'modules';
import * as Yup from 'yup';
import {
  useUpdateUserMutation,
  useGetCompanyAllQuery,
  useGetScreenActionPermissionsQuery,
} from 'services';
import { InputText, InputSelect } from 'components';
import toast from 'react-hot-toast';

import { UpdateUserSuccess, UpdateUserSuccessDefined } from './configs';
import { InputToggleSwitch } from '../../../components/toggle-switch/index';
import { Roles } from 'configs/roles';
import { ScreenActionPermissionItem } from 'services/openapi';
import { getUserRoles } from 'utils/helpers';

interface UpdateUserProps {
  userId: string;
  onClose: () => void;
  forceUpdateTable: () => void;
  refetchUserInfo: () => void;
  refetchLeadersList: () => void;
  dataForm: any;
}

const UpdateForm = ({
  onClose,
  forceUpdateTable,
  dataForm,
  userId,
  refetchUserInfo,
  refetchLeadersList,
}: UpdateUserProps) => {
  const roles = getUserRoles();
  const [isUserActive, setIsUserActive] = React.useState<boolean>(
    dataForm?.isActive,
  );
  const {
    mutateAsync,
    data: dataUpdateUser,
    error: errorUpdateUser,
  } = useUpdateUserMutation();
  const { data: companyInfo } = useGetCompanyAllQuery();
  const { data: dataScreenActionPermissions } =
    useGetScreenActionPermissionsQuery({
      enabled: roles.includes(Roles.SUPER_ADMIN),
    });

  const {
    phoneNumber,
    fullName,
    email,
    role,
    agencyId,
    compId,
    accountNumber,
    bidvVaPayerId,
    citizenIdentityCardNo,
    objectType,
    adminActions,
  } = dataForm || {};

  const dataScreenActionPermissionsLst = useCallback(() => {
    if (!dataScreenActionPermissions) return [];
    return dataScreenActionPermissions?.data?.map(
      (item: ScreenActionPermissionItem) => ({
        label: item.value,
        value: item.key,
      }),
    );
  }, [dataScreenActionPermissions]);

  const normalizedPhoneNumber = phoneNumber.replace(/\+84/, '0');

  useEffect(() => {
    setIsUserActive(dataForm?.isActive ?? false);
  }, [dataForm]);

  const structure = () => {
    return [
      {
        name: 'phoneNumber',
        defaultValue: normalizedPhoneNumber,
        isDisabled: true,
        label: 'Số điện thoại',
        validate: Yup.string()
          .required('Vui lòng nhập số điện thoại')
          .matches(
            /^0\d{9}$/,
            'Số điện thoại không hợp lệ, vui lòng nhập số điện thoại bắt đầu bằng số 0 và có 10 chữ số',
          ),
      },
      {
        name: 'fullName',
        defaultValue: fullName,
        label: 'Tài khoản',
        validate: Yup.string().required('Vui lòng nhập tên tài khoản'),
      },
      {
        name: 'citizenIdentityCardNo',
        defaultValue: citizenIdentityCardNo,
        label: 'CCCD/MST',
        validate: Yup.string()
          .required('Vui lòng nhập CCCD/MST')
          .matches(
            /^\d{6,}$/,
            'CCCD/MST không hợp lệ, vui lòng nhập tối thiểu 6 số',
          ),
      },
      {
        name: 'accountNumber',
        defaultValue: accountNumber,
        isDisabled: true,
        label: 'Tiểu khoản TPBank',
      },
      {
        name: 'bidvVaPayerId',
        defaultValue: bidvVaPayerId,
        isDisabled: bidvVaPayerId ? true : false,
        label: 'Tiểu khoản BIDV',
        validate: Yup.string()
          .required('Vui lòng nhập Tiểu khoản BIDV')
          .min(4, 'Tiểu khoản BIDV phải có ít nhất 4 ký tự')
          .max(10, 'Tiểu khoản BIDV không được vượt quá 10 ký tự')
          .matches(
            /^[A-Z0-9]+$/,
            'Tiểu khoản BIDV phải viết hoa và chỉ chứa chữ cái và số',
          ),
      },
      {
        name: 'email',
        defaultValue: email,
        label: 'Email',
        validate: Yup.string().email('Email không hợp lệ').nullable(),
      },
      {
        name: 'role',
        defaultValue: role === 'Agency' ? objectType : role,
        label: 'Vai trò',
        validate: Yup.string().required('Vui lòng chọn vai trò'),
      },
      {
        name: 'adminActions',
        defaultValue: adminActions,
        // isDisabled: role !== Roles.ADMIN,
        label: 'Thao tác',
      },
      {
        name: 'compId',
        defaultValue: compId,
        label: 'Hợp tác xã',
        // validate: Yup.string()
        //   .when('role', {
        //     is: 'Leader',
        //     then: Yup.string().required('Vui lòng chọn PC'),
        //     otherwise: Yup.string().nullable(),
        //   })
        //   .test({
        //     test: function (value) {
        //       const role = this.resolve(Yup.ref('role'));
        //       if (role === 'SpVisor' || value) {
        //         return true;
        //       }
        //       return false;
        //     },
        //     message: 'Trường này chỉ được bỏ trống khi vai trò là Nhân viên đối soát',
        //   }),
      },
      // {
      //   name: 'agencyId',
      //   defaultValue: agencyId,
      //   label: 'Phòng ban',
      //   // validate: Yup.string()
      //   //   .when('role', {
      //   //     is: 'Agency',
      //   //     then: Yup.string().required('Vui lòng chọn Điện lực'),
      //   //     otherwise: Yup.string().nullable(),
      //   //   })
      //   //   .test({
      //   //     test: function (value) {
      //   //       const role = this.resolve(Yup.ref('role'));
      //   //       if (role === 'SpVisor' || role === 'Leader' || value) {
      //   //         return true;
      //   //       }
      //   //       return false;
      //   //     },
      //   //     message: 'Trường này chỉ được bỏ trống khi vai trò là Nhân viên đối soát hoặc Trưởng nhóm',
      //   //   }),
      // },
    ];
  };

  const { handleSubmit, getInputProps, resetForm, setValues } = useForm({
    enableReinitialize: true,
    structure: structure(),
    onSubmit: async (values) => {
      const normalizedPhoneNumber = values.phoneNumber.replace(/^0/, '+84');
      const normalizedValues = {
        ...values,
        phoneNumber: normalizedPhoneNumber,
        userId: userId,
        isActive: isUserActive,
      };

      if (
        normalizedValues.role !== Roles.SPVISOR &&
        normalizedValues.role !== Roles.ADMIN &&
        normalizedValues.role !== Roles.OIL_MC
      ) {
        normalizedValues.objectType = normalizedValues.role;
        normalizedValues.role = Roles.AGENCY;
      }
      if (normalizedValues.role !== Roles.ADMIN) {
        normalizedValues.adminActions = [];
      }
      if (!normalizedValues.agencyId || normalizedValues.agencyId == '') {
        normalizedValues.agencyId = null;
      }
      if (!normalizedValues.compId || normalizedValues.compId == '') {
        normalizedValues.compId = null;
      }
      try {
        await mutateAsync(normalizedValues);
        resetForm();
        refetchLeadersList();
        refetchUserInfo();
        forceUpdateTable();
        onClose();
      } catch (error) {
        console.log('error', error);
      }
    },
  });

  const dataCompany =
    companyInfo
      // ?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
      ?.map((item) => ({ label: `${item.compName}`, value: item.compId })) ||
    [];

  const dataCompanyWithRoleAgency =
    companyInfo
      // ?.filter((item) => Object.keys(item.compUser).length !== 0)
      // ?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
      ?.map((item) => ({ label: `${item.compName}`, value: item.compId })) ||
    [];

  // const dataCompanyWithRoleLeader =
  //   companyInfo
  //     // ?.filter((item) => Object.keys(item.compUser).length === 0 || item.compId === compId)
  //     ?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];

  let checkedDataCompany: any = [];

  switch (getInputProps('role').value) {
    case 'Agency':
      checkedDataCompany = dataCompanyWithRoleAgency;
      break;

    // case 'Leader':
    //   checkedDataCompany = dataCompanyWithRoleLeader;
    //   break;

    default:
      checkedDataCompany = dataCompany;
      break;
  }

  useEffect(() => {
    if (dataUpdateUser) {
      const { statusCode } = dataUpdateUser;

      const dataDefined =
        UpdateUserSuccessDefined[statusCode as UpdateUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }

    // if (errorUpdateUser) {
    //   const { status } = errorUpdateUser as Error;
    //   toast.error(UpdateUserErrorDefined[(status as UpdateUserError) || 500]?.message);
    // }
  }, [dataUpdateUser, errorUpdateUser]);

  return (
    <>
      <div className='container-fluid Form__Updateuser'>
        <div className='row wrap-form-update-user'>
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('phoneNumber')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('fullName')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('citizenIdentityCardNo')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('accountNumber')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('bidvVaPayerId')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('email')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('role')}
            renderInput={(props) => (
              <InputSelect
                {...props}
                onChange={(e) => {
                  setValues({ compId: '' });
                  props.onChange(e);
                }}
                options={[
                  // { label: 'Đại lý', value: 'Agency' },
                  // { label: 'Trưởng nhóm', value: 'Leader' },
                  ...(roles.includes(Roles.SUPER_ADMIN)
                    ? [{ label: 'Admin', value: 'Admin' }]
                    : []),
                  { label: 'Nhân viên', value: 'Staff' },
                  { label: 'Tài xế', value: 'Driver' },
                  { label: 'Đối tác', value: 'Partner' },
                  { label: 'Nhà cung cấp', value: 'Customer' },
                  // { label: 'Nhân viên đối soát', value: 'SpVisor' },
                  { label: 'Merchant', value: 'OilMc' },
                ]}
              />
            )}
          />
          {getInputProps('role').value === Roles.ADMIN && (
            <InputWraper
              className='col-lg-6'
              inputProps={getInputProps('adminActions')}
              renderInput={(props) => (
                <InputSelect
                  {...props}
                  onChange={(e) => {
                    setValues({ agencyId: '' });
                    props.onChange(e);
                  }}
                  options={dataScreenActionPermissionsLst()}
                  isMulti
                />
              )}
            />
          )}
          {getInputProps('role').value !== Roles.SUPER_ADMIN &&
            getInputProps('role').value !== Roles.ADMIN && (
              <InputWraper
                className='col-lg-6'
                inputProps={getInputProps('compId')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    onChange={(e) => {
                      setValues({ agencyId: '' });
                      props.onChange(e);
                    }}
                    options={checkedDataCompany}
                  />
                )}
              />
            )}
          {/* {getInputProps('compId').value && getInputProps('role').value !== 'Leader' && (
            <InputWraper
              className="col-lg-6"
              inputProps={getInputProps('agencyId')}
              renderInput={(props) => <InputSelect {...props} options={dataAgency} />}
            />
          )} */}
        </div>
        <div className='input'>
          <div className='label'>Trạng thái</div>
          <InputToggleSwitch
            onChange={async (value) => setIsUserActive(value)}
            defaultValue={isUserActive}
            value={isUserActive}
            onTouched={() => false}
            label=''
            name=''
          />
        </div>
      </div>
      <div className='Button__update--user'>
        <Button label='Cập nhật' buttonType='success' onClick={handleSubmit} />
        <Button label='Trở về' buttonType='grey' onClick={() => onClose()} />
      </div>
    </>
  );
};

export default UpdateForm;
